import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore'
import { db } from '../config/firebase.js'

// State reducer for efficient state management and minimal re-renders
const metadataReducer = (state, action) => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, loading: true, error: null }
    case 'SUCCESS':
      return { 
        ...state, 
        metadata: action.payload, 
        loading: false, 
        error: null,
      }
    case 'UPDATE_LOCAL':
      return {
        ...state,
        metadata: { ...state.metadata, [action.field]: action.value },
      }
    case 'SAVING':
      return { ...state, saving: true, error: null }
    case 'SAVE_SUCCESS':
      return { 
        ...state, 
        saving: false, 
        lastSaved: action.timestamp,
        error: null,
      }
    case 'ERROR':
      return { ...state, loading: false, saving: false, error: action.payload }
    case 'RESET':
      return {
        metadata: { nickname: '', location: '', notes: '' },
        loading: false,
        saving: false,
        error: null,
        lastSaved: null,
      }
    default:
      return state
  }
}

/**
 * Firebase v11+ Device Metadata Hook
 * 
 * React 19+ optimized device metadata management with real-time sync.
 * Efficiently manages device nickname, location, and notes with minimal re-renders.
 * 
 * Features:
 * - useReducer for optimal state management
 * - Firestore server timestamps for consistency
 * - Memoized handlers and return values
 * - Real-time sync with snapshot listeners
 * - Atomic save operations with error handling
 * 
 * @param {string} deviceId - Device serial number/ID
 * @returns {Object} Metadata state and update functions
 */
export function useDeviceMetadata(deviceId) {
  const [state, dispatch] = useReducer(metadataReducer, {
    metadata: { nickname: '', location: '', notes: '' },
    loading: false,
    saving: false,
    error: null,
    lastSaved: null,
  })

  // Memoized snapshot handler for real-time updates
  const handleSnapshot = useCallback((doc) => {
    if (doc.exists()) {
      const data = doc.data()
      dispatch({
        type: 'SUCCESS',
        payload: {
          nickname: data.nickname ?? '',
          location: data.location ?? '',
          notes: data.notes ?? '',
        },
      })
    } else {
      // Initialize with deviceId as default nickname if no metadata exists
      dispatch({
        type: 'SUCCESS',
        payload: {
          nickname: deviceId ?? '',
          location: '',
          notes: '',
        },
      })
    }
  }, [deviceId])

  // Memoized error handler
  const handleError = useCallback((err) => {
    console.error('Device metadata listener error:', err)
    dispatch({ 
      type: 'ERROR', 
      payload: err.message || 'Failed to load device metadata',
    })
  }, [])

  // Effect for managing Firestore subscription
  useEffect(() => {
    if (!deviceId?.trim()) {
      dispatch({ type: 'RESET' })
      return
    }

    dispatch({ type: 'LOADING' })
    
    const metadataRef = doc(db, 'deviceMetadata', deviceId)
    const unsubscribe = onSnapshot(metadataRef, handleSnapshot, handleError)
    
    // Cleanup function
    return () => {
      unsubscribe?.()
    }
  }, [deviceId, handleSnapshot, handleError])

  // Memoized local metadata update function
  const updateMetadata = useCallback((field, value) => {
    dispatch({ type: 'UPDATE_LOCAL', field, value })
  }, [])

  // Memoized save function with server timestamps
  const saveMetadata = useCallback(async (metadataToSave = null) => {
    if (!deviceId?.trim()) {
      dispatch({ type: 'ERROR', payload: 'No device selected' })
      return false
    }

    const dataToSave = metadataToSave ?? state.metadata
    dispatch({ type: 'SAVING' })

    try {
      const metadataRef = doc(db, 'deviceMetadata', deviceId)
      const docSnapshot = await getDoc(metadataRef)
      
      const saveData = {
        ...dataToSave,
        deviceId,
        lastUpdated: serverTimestamp(),
      }

      if (docSnapshot.exists()) {
        await updateDoc(metadataRef, saveData)
      } else {
        await setDoc(metadataRef, {
          ...saveData,
          createdAt: serverTimestamp(),
        })
      }

      dispatch({ type: 'SAVE_SUCCESS', timestamp: new Date() })
      return true
    } catch (err) {
      console.error('Error saving device metadata:', err)
      dispatch({ 
        type: 'ERROR', 
        payload: err.message || 'Failed to save device metadata',
      })
      return false
    }
  }, [deviceId, state.metadata])

  // Memoized field-specific save function
  const saveField = useCallback(async (field, value) => {
    const updatedMetadata = { ...state.metadata, [field]: value }
    return await saveMetadata(updatedMetadata)
  }, [state.metadata, saveMetadata])

  // Return memoized state and functions to prevent unnecessary re-renders
  return useMemo(() => ({
    ...state,
    updateMetadata,
    saveMetadata,
    saveField,
  }), [state, updateMetadata, saveMetadata, saveField])
}
